// v224 — Build-Info reverse-lookup UI (item 33 Phase C of
// docs/PRODUCT-ROADMAP.md).
//
// Item 66 (Phase C polish) extended this page with the symmetric
// producer-side answer: "which builds in this project PRODUCED
// log4j-2.14.1?". The role toggle (Consumed / Produced / Both) drives
// the SWR fetch's `role=` parameter; produced rows are rendered with
// a deep-link to the matching registry artifact when the sha256
// landed in the `artifacts` table, which gives a SOC operator a
// direct CVE-triage path: pivot from advisory hit → upstream build →
// registry artifact (with its scan / pull / promotion history) in a
// single click.
//
// Three coordinate-shape modes (orthogonal to role):
//
//   coord  — exact-match on the JFrog coordinate (default)
//   prefix — `log4j%` style prefix-match for "all log4j coords"
//   sha256 — exact-match on content hash
//
// Hits are grouped by build so the operator can see at a glance
// which builds touched the coordinate, then drill into a specific
// build for the per-module trail.

import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  GitBranch,
  Search,
  Layers,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useProjects } from '../../api/client';
import {
  useBuildDependencySearch,
  type DepSearchHit,
  type DepSearchMode,
  type DepSearchResponse,
  type DepSearchRole,
} from '../../api/buildInfo';

function shortRev(rev?: string): string {
  if (!rev) return '';
  return rev.length > 12 ? rev.slice(0, 12) : rev;
}

function roleHelpText(role: DepSearchRole): string {
  if (role === 'consumed') {
    return 'Find builds that DECLARED this coordinate as a dependency. Useful for CVE triage on transitive deps.';
  }
  if (role === 'produced') {
    return 'Find builds that PRODUCED this coordinate as a module artifact. Useful for "who shipped this" SBOM-driven attribution.';
  }
  return 'Show both roles in one timeline: which builds shipped, and which builds consumed, this coordinate.';
}

function modeLabel(m: DepSearchMode): string {
  if (m === 'coord') return 'Exact';
  if (m === 'prefix') return 'Prefix';
  return 'SHA-256';
}

function roleLabel(rr: DepSearchRole): string {
  if (rr === 'consumed') return 'Consumed';
  if (rr === 'produced') return 'Produced';
  return 'Both';
}

function noBuildsRoleText(role: DepSearchRole): string {
  if (role === 'produced') return 'produced';
  if (role === 'any') return 'produced or consumed';
  return 'declared';
}

function matchingBuildsDescription(
  data: DepSearchResponse | undefined,
  groupCount: number,
  role: DepSearchRole,
  producedCount: number,
  consumedCount: number,
): string | undefined {
  if (!data) return undefined;
  const hitsSuffix = data.hits.length === 1 ? '' : 's';
  const buildsSuffix = groupCount === 1 ? '' : 's';
  const roleBreakdown = role === 'any' ? ` · ${producedCount} produced · ${consumedCount} consumed` : '';
  return `${data.hits.length} hit${hitsSuffix} across ${groupCount} build${buildsSuffix}${roleBreakdown}`;
}

interface GroupedHit {
  build_id: string;
  build_name: string;
  build_number: string;
  started: string;
  vcs_url?: string;
  vcs_revision?: string;
  consumed: DepSearchHit[];
  produced: DepSearchHit[];
}

function groupByBuild(hits: DepSearchHit[]): GroupedHit[] {
  const map = new Map<string, GroupedHit>();
  for (const h of hits) {
    let g = map.get(h.build_id);
    if (!g) {
      g = {
        build_id: h.build_id,
        build_name: h.build_name,
        build_number: h.build_number,
        started: h.started,
        vcs_url: h.vcs_url,
        vcs_revision: h.vcs_revision,
        consumed: [],
        produced: [],
      };
      map.set(h.build_id, g);
    }
    if (h.role === 'produced') {
      g.produced.push(h);
    } else {
      g.consumed.push(h);
    }
  }
  return Array.from(map.values());
}

function BuildGroupRow({ g, projectId }: Readonly<{ g: GroupedHit; projectId: string | undefined }>) {
  return (
    <div className="px-5 py-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <Link
            to={`/projects/${projectId}/builds/${encodeURIComponent(g.build_name)}/${encodeURIComponent(g.build_number)}`}
            className="font-medium text-fg hover:text-accent"
          >
            {g.build_name}
            <span className="text-fg-muted font-mono"> #{g.build_number}</span>
          </Link>
          <span className="text-xs text-fg-muted ml-3">
            {new Date(g.started).toLocaleString()}
          </span>
          {g.produced.length > 0 && (
            <Badge tone="success" className="ml-2">
              produced {g.produced.length}
            </Badge>
          )}
          {g.consumed.length > 0 && (
            <Badge tone="neutral" className="ml-2">
              consumed {g.consumed.length}
            </Badge>
          )}
        </div>
        {g.vcs_revision && (
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
            <GitBranch className="h-3.5 w-3.5 shrink-0" />
            <code className="font-mono">{shortRev(g.vcs_revision)}</code>
          </span>
        )}
      </div>
      {g.produced.length > 0 && (
        <ul className="text-xs text-fg-muted space-y-0.5">
          {g.produced.map((row, i) => (
            <li
              key={`prod/${row.module_id}/${row.artifact_name ?? ''}/${i}`}
              className="flex items-center gap-2 flex-wrap"
            >
              <ArrowUpFromLine className="h-3 w-3 text-success shrink-0" />
              <code className="font-mono text-fg">{row.module_id}</code>
              {row.artifact_name && (
                <>
                  <span className="text-fg-subtle">→</span>
                  <code className="font-mono">{row.artifact_name}</code>
                </>
              )}
              {row.dep_type && <Badge tone="neutral">{row.dep_type}</Badge>}
              {row.artifact_id && (
                <Link
                  to={`/artifacts/${encodeURIComponent(row.artifact_id)}`}
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  <Package className="h-3 w-3" />
                  <span className="font-mono">{row.artifact_path || 'in registry'}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
      {g.consumed.length > 0 && (
        <ul className="text-xs text-fg-muted space-y-0.5">
          {g.consumed.map((row, i) => (
            <li
              key={`cons/${row.module_id}/${row.dep_id}/${row.dep_scope ?? ''}/${i}`}
              className="flex items-center gap-2 flex-wrap"
            >
              <ArrowDownToLine className="h-3 w-3 text-fg-subtle shrink-0" />
              <code className="font-mono">{row.module_id}</code>
              <span className="text-fg-subtle">→</span>
              <code className="font-mono text-fg">{row.dep_id}</code>
              {row.dep_scope && <Badge tone="neutral">{row.dep_scope}</Badge>}
              {row.dep_type && <Badge tone="neutral">{row.dep_type}</Badge>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function BuildDependencySearch() {
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const modeParam = (searchParams.get('mode') as DepSearchMode | null) ?? 'coord';
  const mode: DepSearchMode =
    modeParam === 'sha256' || modeParam === 'prefix' ? modeParam : 'coord';
  const roleParam = (searchParams.get('role') as DepSearchRole | null) ?? 'consumed';
  const role: DepSearchRole =
    roleParam === 'produced' || roleParam === 'any' ? roleParam : 'consumed';

  const { data, error, isLoading } = useBuildDependencySearch(id, query, mode, role);
  const groups = useMemo<GroupedHit[]>(
    () => (data ? groupByBuild(data.hits) : []),
    [data],
  );

  const consumedCount = useMemo(
    () => (data ? data.hits.filter((h) => h.role === 'consumed').length : 0),
    [data],
  );
  const producedCount = useMemo(
    () => (data ? data.hits.filter((h) => h.role === 'produced').length : 0),
    [data],
  );

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  const roleHelp = roleHelpText(role);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/projects/${id}/builds`}
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to builds
        </Link>
        <p className="orb-eyebrow text-accent/80 mb-1">
          {project?.slug ?? id} · Provenance
        </p>
        <h1 className="text-2xl font-display font-semibold text-fg leading-tight inline-flex items-center gap-2">
          <Search className="h-5 w-5 text-accent" />
          Reverse dependency search
        </h1>
        <p className="text-fg-muted text-sm mt-2 max-w-3xl">
          Find every CI build in this project that touched a given coordinate —
          either as a consumed dependency or as a produced module artifact.
          Useful for incident response: when a CVE drops on{' '}
          <code className="font-mono">log4j:log4j:2.14.1</code>, this page
          tells you which builds shipped it (producer side) and which
          downstream builds pulled it in (consumer side), so you can
          re-cut every affected pipeline.
        </p>
      </div>

      <Card>
        <CardHeader title="Query" description="Coordinate, prefix, or content hash." />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <Input
              placeholder="e.g. log4j:log4j:2.14.1"
              value={query}
              onChange={(e) => setParam('q', e.target.value)}
            />
            <div className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated p-1">
              {(['coord', 'prefix', 'sha256'] as DepSearchMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setParam('mode', m === 'coord' ? '' : m)}
                  className={
                    'px-3 py-1.5 text-xs rounded-sm transition-colors ' +
                    (mode === m
                      ? 'bg-accent/15 text-accent'
                      : 'text-fg-muted hover:text-fg')
                  }
                >
                  {modeLabel(m)}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-fg-subtle mt-2">
            {mode === 'coord' &&
              'Exact-match on the JFrog coordinate (case-insensitive).'}
            {mode === 'prefix' &&
              'Prefix match — useful for "all log4j-* coordinates" or "every spring-boot version".'}
            {mode === 'sha256' &&
              'Content-hash lookup. Matches dep_sha256 / artifact_sha256 only — coordinates with no recorded checksum are skipped.'}
          </p>

          <div className="mt-4">
            <p className="text-xs font-medium text-fg-muted mb-1.5">Role</p>
            <div className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-elevated p-1">
              {(['consumed', 'produced', 'any'] as DepSearchRole[]).map((rr) => (
                <button
                  key={rr}
                  type="button"
                  onClick={() => setParam('role', rr === 'consumed' ? '' : rr)}
                  className={
                    'px-3 py-1.5 text-xs rounded-sm transition-colors inline-flex items-center gap-1.5 ' +
                    (role === rr
                      ? 'bg-accent/15 text-accent'
                      : 'text-fg-muted hover:text-fg')
                  }
                >
                  {rr === 'consumed' && <ArrowDownToLine className="h-3 w-3" />}
                  {rr === 'produced' && <ArrowUpFromLine className="h-3 w-3" />}
                  {rr === 'any' && <Layers className="h-3 w-3" />}
                  {roleLabel(rr)}
                </button>
              ))}
            </div>
            <p className="text-xs text-fg-subtle mt-2">{roleHelp}</p>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Activity className="h-4 w-4 text-accent" />
              Matching builds
            </span>
          }
          description={matchingBuildsDescription(data, groups.length, role, producedCount, consumedCount)}
        />
        {!query.trim() && (
          <CardBody>
            <p className="text-fg-muted text-sm">
              Enter a coordinate above to search. From a build detail page,
              click any dependency coordinate to land here pre-filled.
            </p>
          </CardBody>
        )}
        {query.trim() && isLoading && (
          <CardBody>
            <p className="text-fg-muted text-sm">Searching…</p>
          </CardBody>
        )}
        {error && (
          <CardBody>
            <p className="text-danger font-mono text-sm">{String(error)}</p>
          </CardBody>
        )}
        {data && groups.length === 0 && !isLoading && (
          <CardBody>
            <p className="text-fg-muted text-sm">
              No builds in this project{' '}
              {noBuildsRoleText(role)}{' '}
              <code className="font-mono">{data.query}</code>
              {role === 'consumed' ? ' as a dependency' : ''}.
            </p>
          </CardBody>
        )}
        {groups.length > 0 && (
          <div className="divide-y divide-border">
            {groups.map((g) => (
              <BuildGroupRow key={g.build_id} g={g} projectId={id} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
